// SCR-592, Activity → Log (REQ-243): the filter bar (kind chips, person, from/to), the timeline
// table (when, who, what, target, kind), a row's detail on click (a guardrail row: outcome,
// model, tool, guardrail, policies, never prompt text), CSV of the visible rows, and Load more.
// The rows come from the sources the shell gives (sources.ts), merged in model.ts; a source that
// fails is a note naming it, and the others still show. Reads only: read-only mode changes
// nothing here. The screen never reads the URL (REQ-201); its sub-tab comes from the shell.
import type { ComponentChildren } from "preact";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { downloadCsv } from "../people/keys";
import { Link } from "../../primitives/link";
import { Card, Chip, FilterBar, type ChipTone } from "../../primitives/page";
import { RowDetail } from "../../primitives/row-detail";
import { NotFound } from "../../primitives/states";
import { DataTable, type Column } from "../../primitives/table";
import {
  ACTIVITY_KINDS,
  activityCsv,
  defaultFilters,
  eventTime,
  KIND_LABELS,
  mergeActivity,
  personOptions,
  toggleKind,
  visibleRows,
  windowError,
  windowOf,
  type ActivityFilters,
  type ActivityKind,
  type ActivityRow,
  type SourceResult,
} from "./model";
import type { ActivitySource } from "./sources";
import { firstVisible, SubTabs } from "../../primitives/subtabs";
import { ACTIVITY_LOG, ACTIVITY_PROMPTS, ACTIVITY_SUBTABS, type ActivityScope } from "./index";

const KIND_TONE: Readonly<Record<ActivityKind, ChipTone>> = {
  settings: "blue",
  quotas: "blue",
  people: "grey",
  keys: "grey",
  sign_in: "grey",
  notifications: "grey",
  guardrail: "red",
  assistant: "green",
};

export const ACTIVITY_LOG_TITLE = "Activity";
export const EMPTY_MESSAGE = "Nothing happened in this window.";

const COLUMNS: readonly Column<ActivityRow>[] = [
  { key: "at", header: "When", cell: (row) => eventTime(row.at), compare: (a, b) => a.at.localeCompare(b.at) },
  {
    key: "who",
    header: "Who",
    cell: (row) => (
      <>
        {row.who}
        {row.whoDetail && <span class="cell-sub">{row.whoDetail}</span>}
      </>
    ),
    compare: (a, b) => a.who.localeCompare(b.who),
  },
  { key: "what", header: "What", cell: (row) => row.what },
  { key: "target", header: "Target", cell: (row) => row.target },
  { key: "kind", header: "Kind", cell: (row) => <Chip tone={KIND_TONE[row.kind]}>{KIND_LABELS[row.kind]}</Chip> },
];

/** Each source's cursor: null once it has no more, absent before its first page. */
type Cursors = ReadonlyMap<ActivitySource, string | null>;

interface Loaded {
  results: SourceResult[];
  cursors: Cursors;
}

/** One page of every source still holding one; a failure is that source's note, not the page's. */
async function loadPages(sources: readonly ActivitySource[], filters: ActivityFilters, cursors: Cursors | null): Promise<Loaded> {
  const window = windowOf(filters);
  const next = new Map<ActivitySource, string | null>();
  const results = await Promise.all(
    sources.map(async (source): Promise<SourceResult> => {
      const cursor = cursors ? cursors.get(source) : null;
      if (cursor === null && cursors !== null) {
        next.set(source, null);
        return { label: source.label, ok: true, rows: [] };
      }
      try {
        const page = await source.load(window, filters, cursor ?? null);
        next.set(source, page.nextCursor);
        return { label: source.label, ok: true, rows: page.items };
      } catch {
        next.set(source, null);
        return { label: source.label, ok: false };
      }
    }),
  );
  return { results, cursors: next };
}

export interface ActivityLogProps {
  sources: readonly ActivitySource[];
  now?: (() => Date) | undefined;
}

export function ActivityLog({ sources, now = () => new Date() }: ActivityLogProps) {
  const [filters, setFilters] = useState<ActivityFilters>(() => defaultFilters(now()));
  const [rows, setRows] = useState<ActivityRow[]>([]);
  const [notes, setNotes] = useState<string[]>([]);
  const [cursors, setCursors] = useState<Cursors | null>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<ActivityRow | null>(null);
  const invalid = windowError(filters);
  // The reads' generation: a new window or source set starts one, and an answer from an older one
  // (a first page or a Load more) is dropped, so a late page never lands under another filter.
  const generation = useRef(0);

  useEffect(() => {
    const mine = ++generation.current;
    if (invalid) return;
    setLoading(true);
    setRows([]);
    setNotes([]);
    setCursors(null);
    void loadPages(sources, filters, null).then((loaded) => {
      if (generation.current !== mine) return;
      const merged = mergeActivity(loaded.results);
      setRows(merged.rows);
      setNotes(merged.notes);
      setCursors(loaded.cursors);
      setLoading(false);
    });
    return () => {
      // Unmounting, or a newer read, retires this one.
      if (generation.current === mine) generation.current++;
    };
    // The person filter is applied to the rows, not the reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sources, filters.kinds, filters.from, filters.to, invalid]);

  const more = cursors !== null && [...cursors.values()].some((cursor) => cursor !== null);
  const loadMore = () => {
    const mine = generation.current;
    setLoading(true);
    void loadPages(sources, filters, cursors).then((loaded) => {
      if (generation.current !== mine) return;
      setRows((old) => mergeActivity([{ label: "", ok: true, rows: old }, ...loaded.results]).rows);
      const merged = mergeActivity(loaded.results);
      setNotes((old) => [...new Set([...old, ...merged.notes])]);
      setCursors(loaded.cursors);
      setLoading(false);
    });
  };

  const shown = useMemo(() => visibleRows(rows, filters), [rows, filters]);
  const people = useMemo(() => personOptions(rows), [rows]);

  return (
    <Card title={ACTIVITY_LOG_TITLE} class="activity-log">
      <FilterBar
        end={
          <button type="button" class="button button-secondary" disabled={shown.length === 0} onClick={() => downloadCsv("nexus-activity.csv", activityCsv(shown))}>
            Download CSV
          </button>
        }
      >
        <div class="activity-kinds" role="group" aria-label="Kind">
          {ACTIVITY_KINDS.map((kind) => (
            <button
              key={kind}
              type="button"
              class="activity-kind"
              aria-pressed={filters.kinds.includes(kind)}
              onClick={() => setFilters((f) => ({ ...f, kinds: toggleKind(f.kinds, kind) }))}
            >
              {KIND_LABELS[kind]}
            </button>
          ))}
        </div>
        <label class="activity-filter">
          <span>Person</span>
          <select value={filters.person ?? ""} onChange={(e) => setFilters((f) => ({ ...f, person: (e.target as HTMLSelectElement).value || null }))}>
            <option value="">Everyone</option>
            {people.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <label class="activity-filter">
          <span>From</span>
          <input type="date" value={filters.from} max={filters.to} onChange={(e) => setFilters((f) => ({ ...f, from: (e.target as HTMLInputElement).value }))} />
        </label>
        <label class="activity-filter">
          <span>To</span>
          <input type="date" value={filters.to} min={filters.from} onChange={(e) => setFilters((f) => ({ ...f, to: (e.target as HTMLInputElement).value }))} />
        </label>
      </FilterBar>

      {invalid && (
        <p class="field-error" role="alert">
          {invalid}
        </p>
      )}
      {notes.map((note) => (
        <p key={note} class="banner banner-warning activity-note" role="status">
          {note}
        </p>
      ))}

      {!invalid && (
        <DataTable
          caption="Activity, newest first"
          hideCaption
          columns={COLUMNS}
          rows={shown}
          rowKey={(row) => row.key}
          loading={loading}
          emptyMessage={EMPTY_MESSAGE}
          nextCursor={more ? "more" : null}
          onLoadMore={loadMore}
          onRowClick={setOpen}
          rowLabel={(row) => `Show ${row.what} at ${eventTime(row.at)}`}
        />
      )}

      {open && <RowDetailOf row={open} onClose={() => setOpen(null)} />}
    </Card>
  );
}

export interface ActivityScreenProps {
  scope: ActivityScope;
  /** The stores this shell reads, in the order their failure notes are listed. */
  sources: readonly ActivitySource[];
  subtab: string | undefined;
  subtabHref(id: string): string;
  now?: (() => Date) | undefined;
  /** The Prompt logs sub-tab's body: the shell's `PromptLogsSubTab`, which explains its own absence. */
  prompts?: ComponentChildren;
}

/**
 * Activity: its sub-tab row, then the Log or the shell's Prompt logs under it. A segment that is
 * neither is not found. Prompts is never unknown: while it is hidden, the shell's body says why.
 */
export function ActivityScreen({ scope, sources, subtab, subtabHref, now, prompts }: ActivityScreenProps) {
  const current = subtab ?? firstVisible(ACTIVITY_SUBTABS, scope);
  return (
    <>
      <SubTabs tabs={ACTIVITY_SUBTABS} scope={scope} current={current} href={subtabHref} label="Activity" />
      {current === ACTIVITY_LOG && <ActivityLog sources={sources} now={now} />}
      {current === ACTIVITY_PROMPTS && prompts}
      {current !== ACTIVITY_LOG && current !== ACTIVITY_PROMPTS && <NotFound />}
    </>
  );
}

function RowDetailOf({ row, onClose }: { row: ActivityRow; onClose(): void }) {
  const facts = [
    { label: "When", value: eventTime(row.at) },
    { label: "Who", value: row.whoDetail ? `${row.who} (${row.whoDetail})` : row.who },
    { label: "What", value: row.what },
    { label: "Target", value: row.target },
    { label: "Kind", value: KIND_LABELS[row.kind] },
    ...row.facts,
  ];
  const prompt = row.guardrail?.promptHref;
  return (
    <RowDetail title={row.what} open onClose={onClose} facts={facts}>
      {prompt && (
        <p>
          <Link href={prompt}>Prompt</Link>
        </p>
      )}
    </RowDetail>
  );
}
